import {
  ACADEMY_LEVELS,
  RARITY_PRESENTATION,
  SUMMONING_SCROLLS,
} from "@ninja/domain";
import { ITEM_ICONS } from "@ninja/asset-catalog";
import { itemDefinition } from "@ninja/game-content";
import { renderNinjaOutfitImage } from "./NinjaOutfitPortrait.js";
import { renderBrandIcon } from "./BrandIcon.js";
import { escapeHtml } from "./escapeHtml.js";
import { formatDuration } from "./formatDuration.js";
import { SectionTabs } from "./SectionTabs.js";
import "./academy.css";

type Rarity = keyof typeof RARITY_PRESENTATION;
type AcademyLevelDefinition = (typeof ACADEMY_LEVELS)[number];
type ApiRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

interface CandidateResult {
  name: string;
  appearanceId: string;
  rarity: Rarity;
  rank: string;
  attributes: Record<string, number>;
  techniqueIds: string[];
}

interface Candidate {
  id: string;
  kind: string;
  result: CandidateResult;
  status: string;
  source_item_id?: string | null;
  created_at?: string;
}

interface CandidateHistoryEntry extends Candidate {
  action: "recruited" | "dismissed";
  resolvedAt: string;
  sourceItemId: string | null;
}

interface AcademyNinja {
  name: string;
  appearance_id: string;
  rarity: Rarity;
  level: number;
}

interface AcademyResponse {
  academy: {
    level: number;
    maxLevel: number;
    foundation_remaining: number;
    regular_ready_at: string | null;
    levelDefinition: AcademyLevelDefinition;
    levelDefinitions: AcademyLevelDefinition[];
  };
  rosterCount: number;
  rosterLimit: number;
}

interface WarehouseResponse {
  items: Array<{
    itemId: string;
    location: string;
    quantity: number;
  }>;
}

interface AcademyPageDependencies {
  request: ApiRequest;
  ninjas: AcademyNinja[];
  onRefresh: () => Promise<void>;
}

export class AcademyPage {
  private readonly listenerController = new AbortController();
  private sectionTabs: SectionTabs | null = null;
  private academy: AcademyResponse | null = null;
  private candidate: Candidate | null = null;
  private history: CandidateHistoryEntry[] = [];
  private scrollQuantities = new Map<string, number>();
  private countdownTimer = 0;
  private candidateRefreshTimer = 0;
  private destroyed = false;
  private recruiting = false;

  constructor(
    private readonly content: HTMLElement,
    private readonly dependencies: AcademyPageDependencies,
  ) {}

  async mount(): Promise<void> {
    this.content.innerHTML =
      '<div class="academy-loading" role="status">Loading Academy…</div>';

    try {
      const [academy, candidateResult, historyResult, warehouse] =
        await Promise.all([
          this.dependencies.request<AcademyResponse>("/academy"),
          this.dependencies.request<{ candidate: Candidate | null }>(
            "/academy/candidate",
          ),
          this.dependencies.request<{ history: CandidateHistoryEntry[] }>(
            "/academy/history",
          ),
          this.dependencies.request<WarehouseResponse>("/warehouse"),
        ]);

      if (this.destroyed || !this.content.isConnected) return;
      this.academy = academy;
      this.candidate = candidateResult.candidate;
      this.history = historyResult.history;
      this.scrollQuantities = this.readScrollQuantities(warehouse);
      this.render();
      this.sectionTabs = new SectionTabs(this.content);
      this.content.addEventListener("click", this.handleClick, {
        signal: this.listenerController.signal,
      });
      this.updateCountdown();
      this.countdownTimer = window.setInterval(
        () => this.updateCountdown(),
        1_000,
      );
      this.candidateRefreshTimer = window.setInterval(
        () => void this.refreshCandidate(),
        15_000,
      );
    } catch (error) {
      if (this.destroyed || !this.content.isConnected) return;
      this.content.innerHTML = `<section class="academy-load-error" role="alert"><h2>Academy could not be loaded</h2><p>${escapeHtml((error as Error).message)}</p></section>`;
    }
  }

  destroy(): void {
    this.destroyed = true;
    window.clearInterval(this.countdownTimer);
    window.clearInterval(this.candidateRefreshTimer);
    this.sectionTabs?.destroy();
    this.listenerController.abort();
  }

  private get rosterFull(): boolean {
    return Boolean(
      this.academy && this.academy.rosterCount >= this.academy.rosterLimit,
    );
  }

  private render(): void {
    const academy = this.academy;
    if (!academy) return;
    this.content.innerHTML = `<div class="page-heading academy-page-heading"><div><p class="eyebrow">THE ACADEMY · RECRUITMENT HALL</p><h1>Recruit and invoke</h1><p class="muted">Resolve each result here: recruit the ninja into your village or dismiss the result.</p></div><button type="button" class="season-badge academy-level-link" data-section-tab-target="status" aria-label="View Academy level effects and progression">ACADEMY LEVEL ${academy.academy.level}/${academy.academy.maxLevel}<span aria-hidden="true">↗</span></button></div>
      <nav class="section-tabs" role="tablist" aria-label="Academy sections">
        <button id="academy-tab-recruitment" class="section-tab" type="button" role="tab" aria-selected="true" aria-controls="academy-panel-recruitment" tabindex="0" data-section-tab="recruitment">Recruitment${this.candidate ? '<span class="section-tab-indicator" aria-label="Pending result">1</span>' : ""}</button>
        <button id="academy-tab-status" class="section-tab" type="button" role="tab" aria-selected="false" aria-controls="academy-panel-status" tabindex="-1" data-section-tab="status">Status</button>
        <button id="academy-tab-history" class="section-tab" type="button" role="tab" aria-selected="false" aria-controls="academy-panel-history" tabindex="-1" data-section-tab="history">History<span class="section-tab-count">${this.history.length}</span></button>
      </nav>

      <section id="academy-panel-recruitment" class="section-tab-panel" role="tabpanel" aria-labelledby="academy-tab-recruitment" data-section-panel="recruitment">
        ${this.renderRecruitment()}
      </section>
      <section id="academy-panel-status" class="section-tab-panel" role="tabpanel" aria-labelledby="academy-tab-status" data-section-panel="status" hidden>
        ${this.renderStatus()}
      </section>
      <section id="academy-panel-history" class="section-tab-panel" role="tabpanel" aria-labelledby="academy-tab-history" data-section-panel="history" hidden>
        ${this.renderHistory()}
      </section>`;
    this.sectionTabs?.sync();
  }

  private renderRecruitment(): string {
    const academy = this.academy!;
    const candidate = this.candidate;
    const readyAt = academy.academy.regular_ready_at;
    const timerMs = readyAt ? Date.parse(readyAt) - Date.now() : 0;
    const timerLabel = candidate
      ? "Paused until this result is resolved"
      : academy.academy.foundation_remaining > 0
        ? "Starts after foundation recruitment"
        : timerMs <= 0
          ? "Ready to reveal"
          : formatDuration(timerMs);
    const recruitLabel =
      academy.academy.foundation_remaining > 0
        ? "Begin guided recruitment"
        : timerMs > 0
          ? "Recruitment charging"
          : "Reveal normal recruit";
    const blockedMessage = candidate
      ? "Resolve the waiting result before invoking another scroll."
      : this.rosterFull
        ? "Your roster is full. Free a Barracks slot before invoking a scroll."
        : "";

    return `<div class="academy-recruitment-layout">
      <section class="academy-panel academy-recruitment-panel">
        <div class="academy-panel-top"><div><p class="eyebrow">${candidate?.kind === "scroll" ? "SCROLL INVOCATION" : candidate?.kind === "foundation" ? "FOUNDATION RECRUITMENT" : "NORMAL RECRUITMENT"}</p><h2>${candidate ? "A shinobi awaits your decision" : "The village calls"}</h2></div><span class="academy-sigil">${renderBrandIcon({ size: "medium" })}</span></div>
        <div id="academy-roulette" class="roulette ${candidate ? "revealed" : ""}">${candidate ? renderCandidateReveal(candidate) : '<div class="roulette-placeholder"><div class="roulette-ring">✦</div><p>YOUR NEXT SHINOBI<br /><b>IS YET TO BE REVEALED</b></p><small>The server determines and persists every result before the animation.</small></div>'}</div>
        <div class="roulette-controls academy-roulette-controls">${candidate ? renderCandidateActions(candidate) : `<button id="academy-recruit" class="button button-primary" ${academy.academy.foundation_remaining === 0 && timerMs > 0 ? "disabled" : ""}>${recruitLabel}<span aria-hidden="true">↗</span></button>`}<p id="academy-notice" class="notice" role="status"></p></div>
        <div class="academy-timer-status"><span class="eyebrow">NEXT NORMAL RECRUITMENT</span><strong id="academy-countdown" data-ready-at="${escapeHtml(readyAt ?? "")}" data-foundation="${academy.academy.foundation_remaining}" data-pending="${candidate ? "true" : "false"}">${timerLabel}</strong></div>
      </section>
      <section class="academy-panel academy-scrolls-panel" aria-labelledby="academy-scrolls-heading">
        <div class="academy-scrolls-heading"><div><p class="eyebrow">INVENTORY CONSUMABLES</p><h2 id="academy-scrolls-heading">Summoning scrolls</h2></div><span class="academy-scrolls-hint">Each scroll creates one result to resolve.</span></div>
        <p class="academy-inline-notice" id="academy-scroll-notice" role="status" ${blockedMessage ? "" : "hidden"}>${escapeHtml(blockedMessage)}</p>
        <div class="academy-scroll-grid">${SUMMONING_SCROLLS.map((scroll) => this.renderScroll(scroll.itemId, scroll.rarity)).join("")}</div>
      </section>
    </div>`;
  }

  private renderStatus(): string {
    const academy = this.academy!;
    const levelRows = academy.academy.levelDefinitions
      .map((definition) =>
        renderAcademyLevelRow(definition, academy.academy.level),
      )
      .join("");
    const recentNinjas = this.dependencies.ninjas
      .slice(-3)
      .map(
        (ninja) =>
          `<div class="academy-recent-ninja"><div class="academy-recent-portrait">${renderNinjaOutfitImage(ninja.appearance_id, ninja.name, { className: "academy-recent-outfit" })}</div><div><b>${escapeHtml(ninja.name)}</b><small>${escapeHtml(ninja.rarity)} · Lv ${ninja.level}</small></div></div>`,
      )
      .join("");

    return `<div class="academy-status-grid">
      <section class="academy-panel academy-status-summary">
        <div class="section-heading"><div><p class="eyebrow">CURRENT EFFECTS · LEVEL ${academy.academy.level}</p><h2>Academy status</h2></div><span class="academy-status-level">${academy.academy.level}/${academy.academy.maxLevel}</span></div>
        <div class="academy-status-metrics"><div><span>Normal recruitment interval</span><b>${formatDuration(academy.academy.levelDefinition.recruitmentIntervalMs)}</b></div><div><span>Guided recruits remaining</span><b>${academy.academy.foundation_remaining} / 4</b></div><div><span>Roster capacity</span><b>${academy.rosterCount} / ${academy.rosterLimit}</b></div></div>
        <p class="academy-status-note">A normal result pauses the next timer until recruited or dismissed. Scroll invocations do not reset this timer.</p>
      </section>
      <section class="academy-panel academy-levels-panel"><div class="section-heading"><div><p class="eyebrow">BUILDING PROGRESSION</p><h2>All Academy levels</h2></div><span class="muted">The highlighted row supplies the live server effects.</span></div><div class="academy-table-wrap"><table class="academy-level-table"><thead><tr><th>LEVEL</th><th>NORMAL TIMER</th><th>COMMON</th><th>UNCOMMON</th><th>RARE</th><th>EPIC</th><th>LEGENDARY</th></tr></thead><tbody>${levelRows}</tbody></table></div></section>
      <section class="academy-panel academy-recent-panel"><div class="section-heading"><div><p class="eyebrow">YOUR SHINOBI</p><h2>Recently recruited</h2></div></div>${recentNinjas || '<div class="empty-state">No ninjas in your roster yet.</div>'}</section>
    </div>`;
  }

  private renderHistory(): string {
    return `<section class="academy-panel academy-history-panel"><div class="section-heading"><div><p class="eyebrow">PERSISTENT RECRUITMENT RECORD</p><h2>Recruitment history</h2><p class="muted">Each result includes its rarity, appearance, source, decision, and decision time.</p></div><span class="muted">${this.history.length} recent</span></div>${this.history.length ? `<div class="academy-history-list">${this.history.map(renderAcademyHistoryEntry).join("")}</div>` : '<div class="empty-state">No recruitment results have been resolved yet.</div>'}</section>`;
  }

  private renderScroll(itemId: string, rarityKey: Rarity): string {
    const definition = itemDefinition(itemId);
    const icon = definition
      ? ITEM_ICONS[definition.iconKey as keyof typeof ITEM_ICONS]
      : undefined;
    const rarity = RARITY_PRESENTATION[rarityKey];
    const quantity = this.scrollQuantities.get(itemId) ?? 0;
    const profile = SUMMONING_SCROLLS.find(
      (scroll) => scroll.itemId === itemId,
    );
    const odds = profile
      ? Object.entries(profile.rarityChances)
          .filter(([, chance]) => chance > 0)
          .map(
            ([resultRarity, chance]) =>
              `${(chance / 100).toFixed(chance % 100 ? 1 : 0)}% ${RARITY_PRESENTATION[resultRarity as Rarity].label}`,
          )
          .join(" · ")
      : "";
    const itemName = definition?.name ?? itemId;
    const accessibleDescription = `${rarity.label} summoning scroll, ${quantity} available. Minimum result rarity: ${rarity.label}. Chances: ${odds || "not available"}.`;
    const blocked = Boolean(this.candidate) || this.rosterFull;

    return `<article class="academy-scroll-card" style="--scroll-rarity:${rarity.color}">
      <div class="academy-scroll-icon" role="img" title="${escapeHtml(`${itemName} · ${accessibleDescription}`)}" aria-label="${escapeHtml(accessibleDescription)}">${icon ? `<img src="/${icon.path}" width="${icon.width}" height="${icon.height}" alt="" />` : "SCROLL"}<span class="academy-scroll-quantity">${quantity}×</span></div>
      <span class="academy-scroll-rarity">${escapeHtml(rarity.label)}</span>
      <button type="button" class="button ${quantity > 0 && !blocked ? "button-primary" : "button-quiet"}" data-invoke-scroll="${escapeHtml(itemId)}" data-quantity="${quantity}" aria-label="Invoke ${escapeHtml(rarity.label)} scroll, ${quantity} available" ${quantity === 0 || blocked ? "disabled" : ""}>Invoke</button>
    </article>`;
  }

  private readScrollQuantities(
    warehouse: WarehouseResponse,
  ): Map<string, number> {
    const quantities = new Map<string, number>();
    for (const scroll of SUMMONING_SCROLLS) quantities.set(scroll.itemId, 0);
    for (const item of warehouse.items) {
      if (item.location === "warehouse" && quantities.has(item.itemId)) {
        quantities.set(
          item.itemId,
          (quantities.get(item.itemId) ?? 0) + item.quantity,
        );
      }
    }
    return quantities;
  }

  private handleClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest<HTMLButtonElement>("#academy-recruit")) {
      void this.startRecruitment();
      return;
    }
    const scrollButton = target.closest<HTMLButtonElement>(
      "[data-invoke-scroll]",
    );
    if (scrollButton?.dataset.invokeScroll) {
      void this.invokeScroll(scrollButton.dataset.invokeScroll, scrollButton);
      return;
    }
    const actionButton = target.closest<HTMLButtonElement>(
      "[data-candidate-action]",
    );
    const action = actionButton?.dataset.candidateAction;
    if (
      actionButton &&
      this.candidate &&
      (action === "recruit" || action === "dismiss")
    ) {
      void this.resolveCandidate(actionButton, action);
    }
  };

  private updateCountdown(): void {
    const countdown =
      this.content.querySelector<HTMLElement>("#academy-countdown");
    const button =
      this.content.querySelector<HTMLButtonElement>("#academy-recruit");
    if (!countdown || !button || !this.academy) return;
    const foundationRemaining = Number(countdown.dataset.foundation ?? 0);
    const pending = countdown.dataset.pending === "true";
    if (pending || this.recruiting) {
      countdown.textContent = "Paused until this result is resolved";
      button.disabled = true;
      return;
    }
    if (foundationRemaining > 0) {
      countdown.textContent = "Starts after foundation recruitment";
      button.disabled = false;
      button.innerHTML =
        'Begin guided recruitment<span aria-hidden="true">↗</span>';
      return;
    }
    const readyAt = Date.parse(countdown.dataset.readyAt ?? "");
    const remaining = Number.isFinite(readyAt) ? readyAt - Date.now() : 0;
    const ready = remaining <= 0;
    countdown.textContent = ready
      ? "Ready to reveal"
      : formatDuration(remaining);
    button.disabled = !ready;
    button.innerHTML = `${ready ? "Reveal normal recruit" : "Recruitment charging"}<span aria-hidden="true">↗</span>`;
  }

  private async startRecruitment(): Promise<void> {
    const button =
      this.content.querySelector<HTMLButtonElement>("#academy-recruit");
    if (!button || !this.academy || this.recruiting) return;
    this.recruiting = true;
    button.disabled = true;
    try {
      const { candidate } = await this.dependencies.request<{
        candidate: Candidate;
      }>("/academy/recruit", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: "{}",
      });
      await this.playReveal(candidate);
      if (!this.destroyed && this.academy.academy.foundation_remaining === 0) {
        this.setNotice(
          "Your server-determined Academy candidate is ready.",
          "success",
        );
      }
    } catch (error) {
      this.setNotice((error as Error).message);
      this.recruiting = false;
      if (button.isConnected) button.disabled = false;
    }
  }

  private async invokeScroll(
    itemId: string,
    button: HTMLButtonElement,
  ): Promise<void> {
    if (button.disabled || this.candidate || this.rosterFull) return;
    button.disabled = true;
    try {
      const { candidate } = await this.dependencies.request<{
        candidate: Candidate;
      }>(`/academy/scrolls/${encodeURIComponent(itemId)}/invoke`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: "{}",
      });
      await this.playReveal(candidate);
      await this.refreshScrollCounts();
    } catch (error) {
      this.setNotice((error as Error).message);
      if (button.isConnected) button.disabled = false;
    }
  }

  private async playReveal(candidate: Candidate): Promise<void> {
    const roulette =
      this.content.querySelector<HTMLElement>("#academy-roulette");
    const controls = this.content.querySelector<HTMLElement>(
      ".academy-roulette-controls",
    );
    if (!roulette || !controls) return;
    this.candidate = candidate;
    this.recruiting = false;
    this.setPendingCountdown(true);
    this.setScrollBlockedNotice();
    roulette.classList.remove("revealed");
    roulette.innerHTML =
      '<div class="roulette-spin"><div class="spin-orbit"><span>COMMON</span><span>UNCOMMON</span><span>RARE</span><span>EPIC</span><span>LEGENDARY</span></div><p>THE ACADEMY IS DRAWING…</p><small>The server already persisted the result.</small></div>';
    roulette.classList.add("spinning");
    await new Promise((resolve) => window.setTimeout(resolve, 1_850));
    if (this.destroyed || !roulette.isConnected || !controls.isConnected)
      return;
    roulette.classList.remove("spinning");
    roulette.classList.add("revealed");
    roulette.innerHTML = renderCandidateReveal(candidate);
    controls.innerHTML = `${renderCandidateActions(candidate)}<p id="academy-notice" class="notice" role="status"></p>`;
  }

  private async resolveCandidate(
    button: HTMLButtonElement,
    action: "recruit" | "dismiss",
  ): Promise<void> {
    const candidate = this.candidate;
    if (!candidate || button.disabled) return;
    const buttons = this.content.querySelectorAll<HTMLButtonElement>(
      "[data-candidate-action]",
    );
    buttons.forEach((entry) => (entry.disabled = true));
    try {
      await this.dependencies.request(
        `/academy/candidates/${candidate.id}/${action}`,
        { method: "POST", body: "{}" },
      );
      await this.dependencies.onRefresh();
    } catch (error) {
      this.setNotice((error as Error).message);
      buttons.forEach((entry) => (entry.disabled = false));
    }
  }

  private async refreshScrollCounts(): Promise<void> {
    const warehouse =
      await this.dependencies.request<WarehouseResponse>("/warehouse");
    if (this.destroyed) return;
    this.scrollQuantities = this.readScrollQuantities(warehouse);
    for (const scroll of SUMMONING_SCROLLS) {
      const button = this.content.querySelector<HTMLButtonElement>(
        `[data-invoke-scroll="${CSS.escape(scroll.itemId)}"]`,
      );
      const icon = button
        ?.closest<HTMLElement>(".academy-scroll-card")
        ?.querySelector<HTMLElement>(".academy-scroll-quantity");
      const quantity = this.scrollQuantities.get(scroll.itemId) ?? 0;
      if (icon) icon.textContent = `${quantity}×`;
      if (button) {
        button.dataset.quantity = String(quantity);
        button.disabled =
          quantity === 0 || Boolean(this.candidate) || this.rosterFull;
        button.classList.toggle(
          "button-primary",
          quantity > 0 && !this.candidate && !this.rosterFull,
        );
        button.classList.toggle(
          "button-quiet",
          quantity === 0 || Boolean(this.candidate) || this.rosterFull,
        );
        button.setAttribute(
          "aria-label",
          `Invoke ${scroll.rarity} scroll, ${quantity} available`,
        );
      }
    }
  }

  private async refreshCandidate(): Promise<void> {
    try {
      const latest = await this.dependencies.request<{
        candidate: Candidate | null;
      }>("/academy/candidate");
      if (!this.destroyed && latest.candidate?.id !== this.candidate?.id) {
        await this.dependencies.onRefresh();
      }
    } catch {
      // A transient poll failure should not interrupt the active Academy screen.
    }
  }

  private setPendingCountdown(pending: boolean): void {
    const countdown =
      this.content.querySelector<HTMLElement>("#academy-countdown");
    if (!countdown) return;
    countdown.dataset.pending = String(pending);
    this.updateCountdown();
  }

  private setScrollBlockedNotice(): void {
    const notice = this.content.querySelector<HTMLElement>(
      "#academy-scroll-notice",
    );
    if (!notice) return;
    notice.hidden = !this.candidate && !this.rosterFull;
    notice.textContent = this.candidate
      ? "Resolve the waiting result before invoking another scroll."
      : this.rosterFull
        ? "Your roster is full. Free a Barracks slot before invoking a scroll."
        : "";
    for (const button of this.content.querySelectorAll<HTMLButtonElement>(
      "[data-invoke-scroll]",
    )) {
      button.disabled =
        Boolean(this.candidate) ||
        this.rosterFull ||
        Number(button.dataset.quantity) === 0;
      button.classList.toggle(
        "button-primary",
        !this.candidate &&
          !this.rosterFull &&
          Number(button.dataset.quantity) > 0,
      );
      button.classList.toggle(
        "button-quiet",
        Boolean(this.candidate) ||
          this.rosterFull ||
          Number(button.dataset.quantity) === 0,
      );
    }
  }

  private setNotice(
    message: string,
    kind: "error" | "success" = "error",
  ): void {
    const element = this.content.querySelector<HTMLElement>("#academy-notice");
    if (element) {
      element.textContent = message;
      element.dataset.kind = kind;
    }
  }
}

function renderCandidateReveal(candidate: Candidate): string {
  const rarity =
    RARITY_PRESENTATION[candidate.result.rarity] ?? RARITY_PRESENTATION.common;
  const source =
    candidate.kind === "foundation"
      ? "Guided foundation recruit"
      : candidate.kind === "scroll"
        ? (itemDefinition(candidate.source_item_id ?? "")?.name ??
          "Summoning scroll")
        : "Normal Academy recruitment";
  return `<div class="candidate-reveal" style="--rarity:${rarity.color}"><div class="candidate-reveal-portrait">${renderNinjaOutfitImage(candidate.result.appearanceId, candidate.result.name, { className: "candidate-reveal-outfit", loading: "eager" })}</div><div class="candidate-rarity">${escapeHtml(rarity.label)}</div><h3>${escapeHtml(candidate.result.name)}</h3><p>${escapeHtml(candidate.result.rank)} · ${escapeHtml(candidate.result.appearanceId)}</p><div class="candidate-stats"><span>HP <b>${candidate.result.attributes.health}</b></span><span>CHAKRA <b>${candidate.result.attributes.chakra}</b></span></div><small>${escapeHtml(source)} · RESULT PERSISTED</small></div>`;
}

function renderCandidateActions(candidate: Candidate): string {
  return `<div class="academy-candidate-actions"><button type="button" class="button button-primary" data-candidate-action="recruit">Recruit ninja</button>${candidate.kind === "foundation" ? "" : '<button type="button" class="button button-quiet" data-candidate-action="dismiss">Dismiss result</button>'}</div>`;
}

function renderAcademyLevelRow(
  definition: AcademyLevelDefinition,
  currentLevel: number,
): string {
  const current = definition.level === currentLevel;
  const chance = (
    rarity: keyof AcademyLevelDefinition["rarityChances"],
  ): string =>
    `${(definition.rarityChances[rarity] / 100).toFixed(definition.rarityChances[rarity] % 100 ? 1 : 0)}%`;
  return `<tr class="${current ? "current" : ""}"><td>${definition.level}${current ? " · CURRENT" : ""}</td><td>${formatDuration(definition.recruitmentIntervalMs)}</td><td>${chance("common")}</td><td>${chance("uncommon")}</td><td>${chance("rare")}</td><td>${chance("epic")}</td><td>${chance("legendary")}</td></tr>`;
}

function renderAcademyHistoryEntry(entry: CandidateHistoryEntry): string {
  const rarity =
    RARITY_PRESENTATION[entry.result.rarity] ?? RARITY_PRESENTATION.common;
  const source =
    entry.kind === "foundation"
      ? "Foundation"
      : entry.kind === "scroll"
        ? (itemDefinition(entry.sourceItemId ?? "")?.name ?? "Summoning scroll")
        : "Academy timer";
  const actionTime = new Date(entry.resolvedAt).toLocaleString();
  return `<article class="academy-history-entry"><div class="academy-history-portrait">${renderNinjaOutfitImage(entry.result.appearanceId, entry.result.name, { className: "academy-history-outfit", loading: "eager" })}</div><div class="academy-history-info"><strong>${escapeHtml(entry.result.name)}</strong><span>${escapeHtml(entry.result.appearanceId)} · ${escapeHtml(source)}</span><time>${escapeHtml(actionTime)}</time></div><span class="academy-history-rarity" style="--history-rarity:${rarity.color}">${escapeHtml(rarity.label)}</span><span class="academy-history-action ${entry.action}">${entry.action === "recruited" ? "Recruited" : "Dismissed"}</span></article>`;
}
